import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { toast } from "sonner";
import { REGIE_COLORS, REGIE_COLOR_FAMILIES } from "./utils";

const EMPTY_REGIE = {
  name: "", firma: "", adresa: "", vorarbeiter: "", report_frequency: "saptamanal", color: REGIE_COLORS[0], active: true, note: "",
  start_date: "", end_date: "", indeterminate_end: false,
  default_min_people: "", default_max_people: "",
  default_min_people_by_day: [null, null, null, null, null, null, null],
  billing_hourly_rate: "",
};

const DEFAULT_FIRMS = ["Implenia", "Speztec", "Klarotec", "Maba", "Imas"];

/**
 * Dialog complet „Adaugă regie" — identic cu cel din /proiecte.
 * Comportament-cheie:
 *  - Firma este OBLIGATORIE; dacă userul încearcă să închidă fără firmă selectată,
 *    dialogul rămâne deschis și afișează un toast roșu.
 *  - „Anulează" explicit închide fără salvare.
 *  - La salvare reușită apelează onCreated(newRegie) pentru ca părintele să refresheze.
 */
export default function RegieFullDialog({ open, onOpenChange, onCreated, onUpdated, defaultStartDate, regieId = null }) {
  const [form, setForm] = useState(EMPTY_REGIE);
  const [existingFirms, setExistingFirms] = useState([]);
  const [allRegii, setAllRegii] = useState([]); // pentru sugestia de familie de culori per firmă
  const [saving, setSaving] = useState(false);
  const isEdit = !!regieId;

  // Familia dominantă de culori folosită deja de alte regii ale aceleiași firme.
  // Returnează numele familiei (ex: "Albastru") + lista de hex-uri DEJA folosite (de evitat).
  const firmFamilyInfo = React.useMemo(() => {
    const firm = (form.firma || "").trim().toLowerCase();
    if (!firm) return { dominantFamily: null, usedColors: new Set(), count: 0 };
    const sameFirm = allRegii.filter((r) => (r.firma || "").trim().toLowerCase() === firm && r.id !== regieId);
    if (sameFirm.length === 0) return { dominantFamily: null, usedColors: new Set(), count: 0 };
    const usedColors = new Set(sameFirm.map((r) => (r.color || "").toUpperCase()));
    // Determină ce familie e majoritară
    const familyCount = {};
    for (const r of sameFirm) {
      const c = (r.color || "").toUpperCase();
      for (const [fam, shades] of Object.entries(REGIE_COLOR_FAMILIES)) {
        if (shades.map((s) => s.toUpperCase()).includes(c)) {
          familyCount[fam] = (familyCount[fam] || 0) + 1;
          break;
        }
      }
    }
    let dominantFamily = null;
    let maxCount = 0;
    for (const [fam, n] of Object.entries(familyCount)) {
      if (n > maxCount) { maxCount = n; dominantFamily = fam; }
    }
    return { dominantFamily, usedColors, count: sameFirm.length };
  }, [form.firma, allRegii, regieId]);

  // Resetare la deschidere; preload firme cunoscute din /regii pentru lista de selecție.
  // Dacă regieId e setat → pre-populăm formularul cu datele regiei existente (mod editare).
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    if (regieId) {
      // Mod EDIT — încărcăm regia existentă
      setForm(EMPTY_REGIE);
      (async () => {
        try {
          const [rAll, rOne] = await Promise.all([
            api.get("/regii"),
            api.get(`/regii/${regieId}`),
          ]);
          if (cancelled) return;
          const allList = rAll.data || [];
          setAllRegii(allList);
          const fromRegii = Array.from(new Set(allList.map((x) => (x.firma || "").trim()).filter(Boolean)));
          setExistingFirms(fromRegii);
          const data = rOne.data || {};
          setForm({
            name: data.name || "",
            firma: data.firma || "",
            adresa: data.adresa || "",
            vorarbeiter: data.vorarbeiter || "",
            report_frequency: data.report_frequency || "saptamanal",
            color: data.color || REGIE_COLORS[0],
            active: data.active !== false,
            note: data.note || "",
            start_date: data.start_date || "",
            end_date: data.end_date || "",
            indeterminate_end: !!data.indeterminate_end,
            default_min_people: data.default_min_people ?? "",
            default_max_people: data.default_max_people ?? "",
            default_min_people_by_day: Array.isArray(data.default_min_people_by_day) && data.default_min_people_by_day.length === 7
              ? data.default_min_people_by_day
              : [null, null, null, null, null, null, null],
            billing_hourly_rate: data.billing_hourly_rate ?? "",
          });
        } catch {
          if (!cancelled) toast.error("Nu am putut încărca datele regiei");
        }
      })();
    } else {
      // Mod CREATE — formular gol
      setForm({ ...EMPTY_REGIE, start_date: defaultStartDate || "", end_date: defaultStartDate || "" });
      (async () => {
        try {
          const r = await api.get("/regii");
          if (cancelled) return;
          const allList = r.data || [];
          setAllRegii(allList);
          const fromRegii = Array.from(new Set(allList.map((x) => (x.firma || "").trim()).filter(Boolean)));
          setExistingFirms(fromRegii);
        } catch {
          if (!cancelled) { setExistingFirms([]); setAllRegii([]); }
        }
      })();
    }
    return () => { cancelled = true; };
  }, [open, defaultStartDate, regieId]);

  const tryClose = (next) => {
    if (next) { onOpenChange(true); return; }
    // Forțăm utilizatorul să selecteze firma înainte de orice închidere implicită (Esc / click outside)
    if (!(form.firma || "").trim()) {
      toast.error("Firma este obligatorie — selectează firma sau apasă Anulează");
      return;
    }
    onOpenChange(false);
  };

  const merged = (() => {
    const arr = Array.from(new Set([...DEFAULT_FIRMS, ...existingFirms])).sort((a, b) => {
      const pri = (n) => {
        const i = DEFAULT_FIRMS.findIndex((d) => d.toLowerCase() === n.toLowerCase());
        return i === -1 ? 99 : i;
      };
      const dp = pri(a) - pri(b);
      return dp !== 0 ? dp : a.localeCompare(b);
    });
    return arr;
  })();

  const currentLower = (form.firma || "").trim().toLowerCase();
  const isInList = merged.some((f) => f.toLowerCase() === currentLower);
  const selValue = !form.firma ? "__none__" : (isInList ? merged.find((f) => f.toLowerCase() === currentLower) : "__custom__");

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Denumire regie obligatorie"); return; }
    if (!(form.firma || "").trim()) { toast.error("Firma este obligatorie — selectează firma unde trimitem oamenii"); return; }
    if (!form.start_date) { toast.error("Data de început este obligatorie"); return; }
    if (!form.indeterminate_end && !form.end_date) { toast.error("Setează data de sfârșit sau bifează Nedeterminat"); return; }
    if (!form.indeterminate_end && form.end_date < form.start_date) {
      toast.error("Data de sfârșit nu poate fi înainte de data de început"); return;
    }
    setSaving(true);
    try {
      const byDay = Array.isArray(form.default_min_people_by_day) ? form.default_min_people_by_day : [];
      const sanitizedByDay = Array.from({ length: 7 }, (_, i) => {
        const v = byDay[i];
        if (v === null || v === undefined || v === "") return null;
        const n = parseInt(v);
        return Number.isFinite(n) && n >= 0 ? n : null;
      });
      const anyDayValueSet = sanitizedByDay.some((v) => v !== null);
      const payload = {
        ...form,
        default_min_people: parseInt(form.default_min_people) || 0,
        default_max_people: parseInt(form.default_min_people) || 0,
        default_min_people_by_day: anyDayValueSet ? sanitizedByDay : null,
        billing_hourly_rate: form.billing_hourly_rate === "" || form.billing_hourly_rate == null ? null : parseFloat(form.billing_hourly_rate),
      };
      if (isEdit) {
        const r = await api.put(`/regii/${regieId}`, payload);
        toast.success("Regie actualizată");
        onOpenChange(false);
        if (onUpdated) onUpdated(r.data);
      } else {
        const r = await api.post("/regii", payload);
        toast.success("Regie adăugată");
        onOpenChange(false);
        if (onCreated) onCreated(r.data);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la salvare");
    } finally { setSaving(false); }
  };

  const dByDay = form.default_min_people_by_day || [];
  const dailyMode = dByDay.some((v) => v !== null && v !== "" && v !== undefined);

  return (
    <Dialog open={open} onOpenChange={tryClose}>
      <DialogContent
        className="max-w-xl max-h-[90vh] overflow-y-auto"
        onInteractOutside={(e) => { if (!(form.firma || "").trim()) e.preventDefault(); }}
        onEscapeKeyDown={(e) => { if (!(form.firma || "").trim()) e.preventDefault(); }}
        data-testid="planificator-regie-dialog"
      >
        <DialogHeader>
          <DialogTitle>{isEdit ? "Editează regia" : "Regie nouă"}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="md:col-span-2">
            <Label>Denumire regie</Label>
            <Input data-testid="planif-regie-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="ex: Regie ACME GmbH"/>
          </div>
          <div className="md:col-span-2">
            <Label>Firmă (la cine trimitem oamenii) <span className="text-red-600">*</span></Label>
            <Select
              value={selValue}
              onValueChange={(v) => {
                if (v === "__add__") {
                  const name = window.prompt("Nume firmă nouă:");
                  if (!name) return;
                  setForm({ ...form, firma: name.trim() });
                  return;
                }
                if (v === "__none__") { setForm({ ...form, firma: "" }); return; }
                if (v === "__custom__") return;
                setForm({ ...form, firma: v });
              }}
            >
              <SelectTrigger className="mt-1" data-testid="planif-regie-firma-select"><SelectValue placeholder="Selectează firma"/></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— Selectează —</SelectItem>
                {merged.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                {!isInList && form.firma && <SelectItem value="__custom__">📝 {form.firma} (custom)</SelectItem>}
                <SelectItem value="__add__">➕ Adaugă firmă nouă…</SelectItem>
              </SelectContent>
            </Select>
            {!form.firma && (
              <div className="mt-1.5 text-[11px] text-red-700 font-semibold" data-testid="planif-regie-firma-warning">
                ⚠ Selectează firma pentru a putea închide acest dialog.
              </div>
            )}
            <input type="hidden" data-testid="planif-regie-firma" value={form.firma || ""} readOnly/>
          </div>
          <div className="md:col-span-2">
            <Label>Adresă proiect</Label>
            <Input data-testid="planif-regie-adresa" value={form.adresa} onChange={(e) => setForm({ ...form, adresa: e.target.value })}/>
          </div>
          <div className="md:col-span-2">
            <Label>👷 Vorarbeiter / Șef echipă</Label>
            <Input data-testid="planif-regie-vorarbeiter" value={form.vorarbeiter} onChange={(e) => setForm({ ...form, vorarbeiter: e.target.value })} placeholder="ex: Hans Müller · +41 79 123 45 67"/>
          </div>
          <div>
            <Label className="small-label">📋 Frecvența raportului de ore</Label>
            <div className="flex gap-2 mt-1">
              {[{ v: "saptamanal", l: "📅 Săptămânal" }, { v: "zilnic", l: "📆 Zilnic" }].map((o) => (
                <button
                  key={o.v}
                  type="button"
                  onClick={() => setForm({ ...form, report_frequency: o.v })}
                  className={`text-xs px-3 py-2 rounded-md border font-semibold transition-colors ${(form.report_frequency || "saptamanal") === o.v ? "bg-[var(--primary)] text-white border-[var(--primary)]" : "bg-white text-zinc-600 border-zinc-300 hover:border-zinc-500"}`}
                  data-testid={`planif-regie-report-freq-${o.v}`}
                >
                  {o.l}
                </button>
              ))}
            </div>
          </div>
          <div className="md:col-span-2">
            <Label>Culoare regie <span className="text-zinc-500 font-normal">— alege o nuanță (grupate pe familii)</span></Label>
            {firmFamilyInfo.dominantFamily && (() => {
              const familyShades = REGIE_COLOR_FAMILIES[firmFamilyInfo.dominantFamily] || [];
              const firstFree = familyShades.find((c) => !firmFamilyInfo.usedColors.has(c.toUpperCase()));
              return (
                <div className="mt-1 mb-1 px-2 py-1.5 rounded bg-amber-50 border border-amber-200 text-[11px] text-amber-900 flex items-start gap-2 flex-wrap">
                  <div className="flex-1 min-w-[180px]">
                    💡 Firma <strong>{form.firma}</strong> folosește deja <strong>{firmFamilyInfo.count}</strong> regii cu familia <strong>{firmFamilyInfo.dominantFamily}</strong>. Recomandare: alege o nuanță liberă din acea familie pentru consistență.
                  </div>
                  {firstFree && (
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, color: firstFree })}
                      className="flex-shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded bg-amber-600 hover:bg-amber-700 text-white text-[11px] font-bold shadow-sm"
                      data-testid="planif-regie-use-recommended"
                      title={`Aplică automat ${firstFree} — prima nuanță liberă din familia ${firmFamilyInfo.dominantFamily}`}
                    >
                      <span className="inline-block w-3 h-3 rounded border border-white/60" style={{ background: firstFree }}/>
                      ★ Folosește nuanța recomandată
                    </button>
                  )}
                  {!firstFree && (
                    <span className="text-[10px] italic text-amber-700">Toate nuanțele din familia {firmFamilyInfo.dominantFamily} sunt deja folosite — alege o familie vecină.</span>
                  )}
                </div>
              );
            })()}
            <div className="mt-1 space-y-1 max-h-[260px] overflow-y-auto pr-1 border border-zinc-200 rounded p-2 bg-zinc-50">
              {Object.entries(REGIE_COLOR_FAMILIES).map(([family, shades]) => {
                const isDominant = firmFamilyInfo.dominantFamily === family;
                return (
                <div key={family} className={`flex items-center gap-2 ${isDominant ? "bg-amber-100/60 rounded -mx-1 px-1 py-0.5" : ""}`}>
                  <span className={`text-[10px] uppercase tracking-wider font-bold w-[80px] flex-shrink-0 ${isDominant ? "text-amber-900" : "text-zinc-600"}`}>
                    {family}{isDominant && " ★"}
                  </span>
                  <div className="flex flex-wrap gap-1">
                    {shades.map((c) => {
                      const isUsed = firmFamilyInfo.usedColors.has(c.toUpperCase());
                      return (
                        <button
                          key={c}
                          type="button"
                          onClick={() => setForm({ ...form, color: c })}
                          className={`w-6 h-6 rounded border-2 transition-all relative ${form.color === c ? "border-zinc-900 scale-110 ring-2 ring-offset-1 ring-zinc-400" : "border-white hover:scale-110 hover:border-zinc-300"} ${isUsed ? "opacity-50" : ""}`}
                          style={{ background: c }}
                          data-testid={`planif-regie-color-${c}`}
                          title={isUsed ? `${family} ${c} — folosit deja de altă regie a firmei` : `${family} ${c}`}
                        >
                          {isUsed && <span className="absolute inset-0 flex items-center justify-center text-[8px] text-white font-bold drop-shadow">✓</span>}
                        </button>
                      );
                    })}
                  </div>
                </div>
                );
              })}
            </div>
            <div className="text-[10px] text-zinc-500 mt-1">
              Culoarea aleasă: <span className="inline-block w-3 h-3 rounded align-middle border border-zinc-300" style={{ background: form.color }}/> <code className="font-mono">{form.color}</code>
              {firmFamilyInfo.dominantFamily && <span className="ml-2">★ familia recomandată · ✓ deja folosită de altă regie a firmei</span>}
            </div>
          </div>
          <div className="md:col-span-2 flex items-center gap-3">
            <Label className="small-label">Activă</Label>
            <Switch checked={form.active !== false} onCheckedChange={(v) => setForm({ ...form, active: v })}/>
            <span className="text-xs text-zinc-500">Regiile inactive nu apar în planul lunar</span>
          </div>
          <div className="md:col-span-2"><Label>Notă</Label><Textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })}/></div>
          <div className="md:col-span-2 border-t pt-3 mt-1">
            <div className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">⏱ Interval & necesar</div>
          </div>
          <div>
            <Label>Data început</Label>
            <Input type="date" data-testid="planif-regie-start-date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })}/>
          </div>
          <div>
            <Label>Data sfârșit</Label>
            <Input type="date" data-testid="planif-regie-end-date" value={form.end_date} disabled={form.indeterminate_end} onChange={(e) => setForm({ ...form, end_date: e.target.value })}/>
            <div className="flex items-center gap-2 mt-1.5">
              <Switch data-testid="planif-regie-indeterminate" checked={!!form.indeterminate_end}
                onCheckedChange={(v) => setForm({ ...form, indeterminate_end: v, end_date: v ? "" : form.end_date })}/>
              <Label className="small-label">Nedeterminat</Label>
            </div>
          </div>
          <div className="md:col-span-2 border-2 border-amber-200 bg-amber-50/50 rounded-lg p-3">
            <div className="flex items-center justify-between mb-2">
              <Label className="small-label text-amber-800 font-bold">👥 Minim necesar personal</Label>
              <div className="flex rounded-md border border-amber-300 overflow-hidden text-[10px] font-bold uppercase tracking-wider">
                <button type="button" onClick={() => setForm({ ...form, default_min_people_by_day: [null, null, null, null, null, null, null] })}
                  className={`px-2 py-1 transition-colors ${!dailyMode ? "bg-amber-600 text-white" : "bg-white text-amber-700 hover:bg-amber-100"}`}
                  data-testid="planif-regie-min-mode-weekly">📅 Săptămânal</button>
                <button type="button" onClick={() => {
                    const base = parseInt(form.default_min_people) || 0;
                    setForm({ ...form, default_min_people_by_day: [base, base, base, base, base, 0, 0] });
                  }}
                  className={`px-2 py-1 transition-colors ${dailyMode ? "bg-amber-600 text-white" : "bg-white text-amber-700 hover:bg-amber-100"}`}
                  data-testid="planif-regie-min-mode-daily">📊 Zilnic</button>
              </div>
            </div>
            {!dailyMode ? (
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="small-label">Minim personal (Lu-Vi)</Label>
                  <Input type="number" min={0} placeholder="ex: 3" data-testid="planif-regie-min-people" value={form.default_min_people} onChange={(e) => setForm({ ...form, default_min_people: e.target.value })}/>
                </div>
              </div>
            ) : (
              <div>
                <div className="grid grid-cols-7 gap-1.5">
                  {["Lu", "Ma", "Mi", "Jo", "Vi", "Sâ", "Du"].map((d, i) => (
                    <div key={d} className="flex flex-col items-center">
                      <span className="text-[10px] font-bold text-amber-700 mb-0.5">{d}</span>
                      <Input type="number" min={0} placeholder="—" value={dByDay[i] ?? ""}
                        onChange={(e) => {
                          const next = [...(form.default_min_people_by_day || [null, null, null, null, null, null, null])];
                          const raw = e.target.value;
                          next[i] = raw === "" ? null : Math.max(0, parseInt(raw) || 0);
                          setForm({ ...form, default_min_people_by_day: next });
                        }}
                        className="h-9 text-center text-sm font-bold tabular-nums px-1"
                        data-testid={`planif-regie-min-day-${i}`}/>
                    </div>
                  ))}
                </div>
                <div className="text-[10px] text-zinc-600 mt-1.5">
                  💡 Lasă o zi goală pentru a folosi minimul săptămânal implicit ({parseInt(form.default_min_people) || 0} pers).
                </div>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <div>
                    <Label className="small-label">Min săptămânal (fallback)</Label>
                    <Input type="number" min={0} placeholder="ex: 3" data-testid="planif-regie-min-people" value={form.default_min_people} onChange={(e) => setForm({ ...form, default_min_people: e.target.value })}/>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="md:col-span-2 text-[11px] text-zinc-500">
            💡 Necesarul se aplică zilnic pe toată durata regiei. La salvare, intervalul ales se creează automat în calendar.
          </div>
          <div className="md:col-span-2 border-t border-zinc-200 pt-3 mt-1">
            <Label className="small-label flex items-center gap-1.5">
              💰 Tarif orar de FACTURARE (CHF/oră)
              <span className="text-[10px] font-normal text-zinc-500">— folosit la calculul totalului pe pagina Facturare</span>
            </Label>
            <Input
              type="number" step="0.5" min={0}
              placeholder="ex: 65"
              value={form.billing_hourly_rate}
              onChange={(e) => setForm({ ...form, billing_hourly_rate: e.target.value })}
              data-testid="planif-regie-billing-rate"
              className="mt-1"
            />
            <div className="text-[10px] text-zinc-500 mt-1">
              Lasă gol dacă nu vrei calcul automat — vor apărea doar orele lucrate.
            </div>
          </div>
        </div>
        <DialogFooter className="gap-2 flex-wrap">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="planif-regie-cancel">Anulează</Button>
          <Button onClick={submit} disabled={saving} data-testid="planif-regie-submit" className="bg-[var(--orange-bila)] hover:opacity-90">
            {saving ? "Se salvează…" : "Salvează"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
